import React from 'react';
import { useField } from 'react-final-form';
import isEmail from 'validator/lib/isEmail';
import typography from '@tcl-boron-styles/typography/dist/index.module.css';
import {
  NAME_CHARACTER_LIMIT,
  PATIENT_CONSENT_LABEL_V4,
  PATIENT_REPRESENTATIVE_CONSENT_LABEL_V4,
  RELATIONSHIP_TO_PATIENT_NAME_V4,
  REVOKE_ASSISTANCE_DISCLAIMER_LABEL_V4,
  RelationshipToPatientV4,
  consentToApplicationV4Fields as consentToApplicationV4FieldsBase,
  getFormVersionDropdownOptions,
  getRelationshipToPatientV4DropdownOptions,
} from '@tempus/patient-forms-service-shared';

import FormTable, { FormTableFieldFrontend } from '../../shared/FormTable';
import { combineFormConfigs, isValidPhoneNumber } from '../../shared/utils';
import {
  CHARACTER_LIMIT_WARNING,
  INVALID_EMAIL_WARNING,
  INVALID_PHONE_WARNING,
  REQUIRED_FIELD_WARNING,
} from '../../shared/validation-utils';
import { featureEnabledInEnvironment } from '../../../../../config/feature-toggles';
import TempusRepresentativeAuthBadge from '../shared/TempusRepresentativeAuthBadge';

const PatientConsentCheckboxLabel: React.FunctionComponent = () => {
  const { input } = useField<RelationshipToPatientV4>(RELATIONSHIP_TO_PATIENT_NAME_V4, {
    subscription: { value: true },
  });

  // null case should never hit but included for safety
  return (
    <span>
      {input.value === RelationshipToPatientV4.PATIENT_REPRESENTATIVE
        ? PATIENT_REPRESENTATIVE_CONSENT_LABEL_V4
        : input.value === RelationshipToPatientV4.PATIENT
        ? PATIENT_CONSENT_LABEL_V4
        : null}
    </span>
  );
};

export const consentToApplicationV4FieldsFrontend: FormTableFieldFrontend[] = [
  {
    name: RELATIONSHIP_TO_PATIENT_NAME_V4,
    props: {
      placeholder: 'Select',
      dropdownOptions: getRelationshipToPatientV4DropdownOptions(),
      ...(featureEnabledInEnvironment('enableFaConsentFormVersion') ? { Badge: TempusRepresentativeAuthBadge } : {}),
    },
    validate: (value: any | undefined) => {
      if (!value) {
        return REQUIRED_FIELD_WARNING;
      }
    },
  },
  {
    name: 'representativeFirstName',
    validate: (value: string | undefined, allValues: Record<string, any>): string | undefined => {
      if (!value && allValues[RELATIONSHIP_TO_PATIENT_NAME_V4] === RelationshipToPatientV4.PATIENT_REPRESENTATIVE) {
        return REQUIRED_FIELD_WARNING;
      } else if (String(value).length > NAME_CHARACTER_LIMIT) {
        return CHARACTER_LIMIT_WARNING;
      }
    },
    conditionalValueConfig: {
      fieldNameToCheck: RELATIONSHIP_TO_PATIENT_NAME_V4,
      valueToCheck: [RelationshipToPatientV4.TEMPUS_REPRESENTATIVE, RelationshipToPatientV4.PATIENT_REPRESENTATIVE],
      conditionalLogic: (value: RelationshipToPatientV4, conditionalValue: RelationshipToPatientV4[]): boolean => {
        return conditionalValue.includes(value as RelationshipToPatientV4);
      },
    },
  },
  {
    name: 'representativeLastName',
    validate: (value: string | undefined, allValues: Record<string, any>): string | undefined => {
      if (!value && allValues[RELATIONSHIP_TO_PATIENT_NAME_V4] === RelationshipToPatientV4.PATIENT_REPRESENTATIVE) {
        return REQUIRED_FIELD_WARNING;
      } else if (String(value).length > NAME_CHARACTER_LIMIT) {
        return CHARACTER_LIMIT_WARNING;
      }
    },
    conditionalValueConfig: {
      fieldNameToCheck: RELATIONSHIP_TO_PATIENT_NAME_V4,
      valueToCheck: [RelationshipToPatientV4.TEMPUS_REPRESENTATIVE, RelationshipToPatientV4.PATIENT_REPRESENTATIVE],
      conditionalLogic: (value: RelationshipToPatientV4, conditionalValue: string): boolean => {
        return conditionalValue.includes(value as RelationshipToPatientV4);
      },
    },
  },
  {
    name: 'tempusRepresentativeFirstName',
    validate: (value: any | undefined) => {
      if (!value) {
        return REQUIRED_FIELD_WARNING;
      } else if (String(value).length > NAME_CHARACTER_LIMIT) {
        return CHARACTER_LIMIT_WARNING;
      }
    },
    conditionalValueConfig: {
      fieldNameToCheck: RELATIONSHIP_TO_PATIENT_NAME_V4,
      valueToCheck: RelationshipToPatientV4.TEMPUS_REPRESENTATIVE,
      conditionalLogic: (value, conditionalValue): boolean => {
        return value === conditionalValue;
      },
    },
  },
  {
    name: 'tempusRepresentativeLastName',
    validate: (value: any | undefined) => {
      if (!value) {
        return REQUIRED_FIELD_WARNING;
      } else if (String(value).length > NAME_CHARACTER_LIMIT) {
        return CHARACTER_LIMIT_WARNING;
      }
    },
    conditionalValueConfig: {
      fieldNameToCheck: RELATIONSHIP_TO_PATIENT_NAME_V4,
      valueToCheck: RelationshipToPatientV4.TEMPUS_REPRESENTATIVE,
      conditionalLogic: (value, conditionalValue): boolean => {
        return value === conditionalValue;
      },
    },
  },
  {
    name: 'representativeEmail',
    validate: (value: any | undefined) => {
      if (value && !isEmail(value)) {
        return INVALID_EMAIL_WARNING;
      }
    },
    conditionalValueConfig: {
      fieldNameToCheck: RELATIONSHIP_TO_PATIENT_NAME_V4,
      valueToCheck: [RelationshipToPatientV4.TEMPUS_REPRESENTATIVE, RelationshipToPatientV4.PATIENT_REPRESENTATIVE],
      conditionalLogic: (value: RelationshipToPatientV4, conditionalValue: RelationshipToPatientV4[]): boolean => {
        return conditionalValue.includes(value as RelationshipToPatientV4);
      },
    },
  },
  {
    name: 'representativePhone',
    props: {
      placeholder: '(XXX)XXX-XXXX',
    },
    validate: (value: any | undefined) => {
      if (value && !isValidPhoneNumber(value)) {
        return INVALID_PHONE_WARNING;
      }
    },
    conditionalValueConfig: {
      fieldNameToCheck: RELATIONSHIP_TO_PATIENT_NAME_V4,
      valueToCheck: [RelationshipToPatientV4.TEMPUS_REPRESENTATIVE, RelationshipToPatientV4.PATIENT_REPRESENTATIVE],
      conditionalLogic: (value: RelationshipToPatientV4, conditionalValue: RelationshipToPatientV4): boolean => {
        return conditionalValue.includes(value as RelationshipToPatientV4);
      },
    },
  },
  ...(featureEnabledInEnvironment('enableFaConsentFormVersion')
    ? [
        {
          name: 'formVersion',
          props: {
            placeholder: 'Select',
            dropdownOptions: getFormVersionDropdownOptions(),
          },
          validate: (value: any | undefined) => {
            if (!value) {
              return REQUIRED_FIELD_WARNING;
            }
          },
          conditionalValueConfig: {
            fieldNameToCheck: RELATIONSHIP_TO_PATIENT_NAME_V4,
            valueToCheck: RelationshipToPatientV4.TEMPUS_REPRESENTATIVE,
            conditionalLogic: (value: RelationshipToPatientV4, conditionalValue: RelationshipToPatientV4): boolean => {
              return value === conditionalValue;
            },
          },
        },
      ]
    : []),
  {
    name: 'patientConsent',
    props: {
      checkboxLabel: <PatientConsentCheckboxLabel />,
    },
    validate: (value: any | undefined) => {
      if (!value) {
        return REQUIRED_FIELD_WARNING;
      }
    },
    conditionalValueConfig: {
      fieldNameToCheck: RELATIONSHIP_TO_PATIENT_NAME_V4,
      valueToCheck: [RelationshipToPatientV4.PATIENT, RelationshipToPatientV4.PATIENT_REPRESENTATIVE],
      conditionalLogic: (value: RelationshipToPatientV4, conditionalValue: RelationshipToPatientV4[]): boolean => {
        return conditionalValue.includes(value as RelationshipToPatientV4);
      },
    },
  },
  {
    name: 'patientRepresentativeConsent',
    validate: (value: any | undefined) => {
      if (!value) {
        return REQUIRED_FIELD_WARNING;
      }
    },
    conditionalValueConfig: {
      fieldNameToCheck: RELATIONSHIP_TO_PATIENT_NAME_V4,
      valueToCheck: RelationshipToPatientV4.TEMPUS_REPRESENTATIVE,
      conditionalLogic: (value: any, conditionalValue: string) => {
        return value === conditionalValue;
      },
    },
  },
  {
    name: 'signature',
    validate: (value: any | undefined) => {
      if (!value) {
        return REQUIRED_FIELD_WARNING;
      }
    },
  },
];

export const consentToApplicationV4Fields = combineFormConfigs(
  consentToApplicationV4FieldsFrontend,
  consentToApplicationV4FieldsBase,
);

const ConsentToApplicationV4Table: React.FunctionComponent = () => {
  return (
    <>
      <div className={typography.subSectionHeader}>Consent to Application</div>
      <FormTable fields={consentToApplicationV4Fields} />
      <em>{REVOKE_ASSISTANCE_DISCLAIMER_LABEL_V4}</em>
    </>
  );
};

export default ConsentToApplicationV4Table;
